import React, {useEffect, useState, ReactNode} from 'react'
import {DialogContent, DialogOverlay} from '@reach/dialog'
import {Box, Button, useDisclosure, Stack, Flex} from '../../../vanilla'
import {useOverlayTransition} from '../../../vanilla/hooks/useOverlayTransition'
import {CloseIcon, DownloadAppIcon} from '../../icons'
import {useCustomerStore, useGlobalStore, useOrderStore} from '../../../store/hooks/useStore'
import {observer} from 'mobx-react-lite'
import {MobileNavigationList} from './mobile-navigation-list'
import {vars} from '../../../vanilla/vars.css'
import {MobileNavigationLoggedLinks} from './mobile-navigation-logged-links'
import {MobileNavigationLinks} from './mobile-navigation-links'
import {DeliverySlot} from './delivery-slot'
import {LoginButtons} from './login-buttons'
import {getMobileDeviceOS, calculateDrawerWidth} from '../../../utils/utils'
import {useLocation} from 'react-router-dom'
import {ExitOrderEditModeSignOutModal} from '../../edit-order-exit-mode-modal'
import {closeButton} from '../styles.css'
import useNavigation from '../../../hooks/use-navigation'
import BonusCardApp from '../../../utils/app'
import {useSupportPinsEnabled} from '../../../hooks/use-support-pins-enabled'

interface MobileNavigationDrawerProps extends ReturnType<typeof useDisclosure> {
  positionRef: React.RefObject<Element>
}
interface MobileStaticNavigationListProps {
  mobileNavLinks: Array<{link: string; name: string; icon?: ReactNode; key: number}>
  onClose: () => void
}

export const TRANSITION_DURATION = 200

const MobileNavigationDrawer = observer(
  ({isOpen, onClose, positionRef}: MobileNavigationDrawerProps) => {
    const {bypassFocusLock, isVisible, isReady} = useOverlayTransition({
      isOpen,
      duration: TRANSITION_DURATION
    })
    const [positionTop, setPositionTop] = useState(0)
    const drawerWidth = calculateDrawerWidth()

    const {
      storeAndDaySpecificNavigation,
      getCustomSitePreferenceById,
      isCustomSitePreferenceSet,
      customSitePreferences
    } = useGlobalStore()
    const {isRegistered, logout} = useCustomerStore()
    const [appCustomPreference, setAppCustomPreference] = useState<string>('none')
    const device = getMobileDeviceOS()
    const location = useLocation()
    const navigate = useNavigation()
    const signOutExitOrderEditMode = useDisclosure(false)
    const {editMode} = useOrderStore()
    const app = new BonusCardApp()
    const {isSupportPinsEnabled} = useSupportPinsEnabled()

    // Get the right custom preference depending on the device
    useEffect(() => {
      if (device === 'Android') {
        (async function () {
          if (!isCustomSitePreferenceSet('navAppLinkAndroid')) {
            await getCustomSitePreferenceById('navAppLinkAndroid')
          }
          setAppCustomPreference(customSitePreferences['navAppLinkAndroid'] as string)
        })()
      }
      if (device === 'iOS') {
        (async function () {
          if (!isCustomSitePreferenceSet('navAppLinkApple')) {
            await getCustomSitePreferenceById('navAppLinkApple')
          }
          setAppCustomPreference(customSitePreferences['navAppLinkApple'] as string)
        })()
      }
    }, [device])

    const mobileNavLinks: Array<{link: string; name: string; icon?: ReactNode; key: number}> = [
      {
        link: `${appCustomPreference}`,
        name: 'Download the Iceland App',
        icon: <DownloadAppIcon boxSize="16px" />
      },
      {link: '/customer-support/contact-us', name: 'Contact Us / FAQs'},
      ...(isSupportPinsEnabled ? [{link: '/account/support-pin', name: 'Support PIN'}] : []),
      {link: '/free-delivery.html', name: 'Free Delivery'},
      {link: 'https://about.iceland.co.uk/', name: 'About Iceland'},
      {link: '/store-finder', name: 'Store Finder '}
    ].map((item, index) => ({...item, key: index}))

    // When opening, find the bottom location of the positionRef to use
    // as our `top` position.
    useEffect(() => {
      if (isReady && !isVisible) {
        const rect = positionRef?.current?.getBoundingClientRect()
        if (rect) {
          setPositionTop(rect.top)
        }
      }
    }, [isReady])

    // Do not render anything unless the component is being used. This tracks
    // the `isOpen` prop and delays it to allow for enter/exit transitions.
    // See the `useOverlayTransition` for more detail.
    if (!isReady) {
      return null
    }

    if (!storeAndDaySpecificNavigation) {
      return null
    }

    const logoutHandler = () => {
      if (editMode) {
        signOutExitOrderEditMode.onOpen()
        return
      }

      logout()
      // Redirect to home if the user is on the account page
      if (location.pathname.includes('account')) {
        navigate('/')
      }
      onClose()
    }

    return (
      <>
        <ExitOrderEditModeSignOutModal {...signOutExitOrderEditMode} navMenuOnClose={onClose} />
        <DialogOverlay
          dangerouslyBypassFocusLock={bypassFocusLock}
          isOpen={isReady}
          onDismiss={onClose}
          style={{
            height: `calc(100vh - ${positionTop})`,
            top: positionTop,
            left: 0,
            transition: `background-color ${TRANSITION_DURATION}ms ease-out`,
            backgroundColor: isVisible ? '#00000066' : '#00000000',
            zIndex: vars.zIndex.overlay
          }}
        >
          <Flex gap="10px">
            <Box
              as={DialogContent}
              aria-label="Mobile navigation"
              id="mobile-nav-box"
              style={{
                transform: `translateX(${isVisible ? 0 : -drawerWidth}px)`
              }}
            >
              <Stack position="relative" gap="0px" height="full">
                <Box height="full" overflowY="auto" overflowX="hidden">
                  <Stack position="relative" gap="8px" paddingTop="12px" id="mobile-nav-stack">
                    <DeliverySlot onClose={onClose} />
                    {!isRegistered && <LoginButtons />}
                    <MobileNavigationLoggedLinks onClose={onClose} />
                    <MobileNavigationList nav={storeAndDaySpecificNavigation} onClose={onClose} />
                    <MobileStaticLinksMenu mobileNavLinks={mobileNavLinks} onClose={onClose} />
                    {isRegistered && !app.isApp() && (
                      <Box paddingX="16px" paddingBottom="20px">
                        <Button
                          variant="outlinePrimary"
                          onClick={() => {
                            logoutHandler()
                            onClose()
                          }}
                          width="full"
                          size="sm"
                          marginTop="8px"
                          data-test-selector="sign-out-btn"
                        >
                          Sign Out
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
            <Button
              variant="unstyled"
              className={closeButton}
              onClick={onClose}
              iconLeft={<CloseIcon color="black" boxSize="28px" />}
              // Fade in in line with #mobile-nav-box
              style={{
                opacity: isVisible ? 1 : 0
              }}
            />
          </Flex>
        </DialogOverlay>
      </>
    )
  }
)

const MobileStaticLinksMenu = ({mobileNavLinks, onClose}: MobileStaticNavigationListProps) => {
  return <MobileNavigationLinks mobileNavLinks={mobileNavLinks} onClose={onClose} />
}

export default MobileNavigationDrawer
